import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Checkbox,
  Flex,
  Heading,
  Text,
  useToast,
} from "@chakra-ui/react";
import { useFrameworkNoLongerActive } from "../../../../../sourcing-event/FrameworkNoLongerActive";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ChangedArea,
  FieldKeys,
  SourcingEventStatus,
  useGetProductGroupsForSourcingEventQuery,
  useListTextDocumentsForSourcingEventQuery,
  useGetSourcingEventQuery,
  usePublishPrivateSourcingEventMutation,
  useGetFrameworkAgreementQuery,
  useCancelEventChangeMutation,
  useSubmitEventChangeMutation,
  type FieldStatusDto,
  type SourcingEventDto,
  type SourcingEventPatchDto,
} from "../../../../../../autogen/bff-api";
import { coordinatorRegistry, useEditor } from "../../../../../../common/editing/save-coordinator";
import { useApiError } from "../../../../../../common/errors/useApiError";
import { useExclusiveAction } from "../../../../../../common/hooks/useExclusiveAction";
import { displayDateWithTime } from "../../../../../../common/formatting/displayDateWithTime";
import { TipTap, isTiptapContentEmpty } from "../../../../../../common/input/TipTap/TipTap";
import { StepNavigation } from "../../../../../../common/StepNavigation";
import { useSub } from "../../../../../../common/subscription/useSub";
import { useConfirmationModal } from "../../../../../../common/useConfirmationModal";
import { urls } from "../../../../../../urls";
import { useProcurementTerms } from "../../../../../../common/terminology/useProcurementTerms";
import { EmptyValue, SummaryField } from "../../components/summary/SummaryField";
import { useReadinessByField } from "../../hooks/useReadinessByField";
import { openChangeDraft } from "../../../../../sourcing-event/changes/changeScope";
import { invitedOrganizations } from "../../../../../sourcing-event/invitedOrganizations";
import {
  activePanel,
  reachableUnregistered,
  tooFewForAMiniCompetition,
} from "../../../../../framework-agreement/callOffAvailability";
import { InvitationChangeList } from "./InvitationChangeList";
import { useSettledTerms } from "../mini-competition/useSettledTerms";
import { invitationChanges } from "./invitationChanges";
import { isFetchBaseQueryError } from "../../../../../../common/errors/isFetchBaseQueryError";
import { isApiErrorDto } from "../../../../../../common/errors/isApiErrorDto";
import { includesAwardCriteria, includesMinimumRequirements, resolveInclusions } from "../../../../../../common/sourcing-events/buyer/wizardSteps";

interface Props {
  eventId: string;
  previousStep?: () => void;
  openInvitations?: () => void;
}

const orderedByUnmet = (statuses: ReadonlyArray<FieldStatusDto | undefined>) => [
  ...statuses.filter((status) => status && !status.satisfied),
  ...statuses.filter((status) => status?.satisfied),
];

export const ReviewAndPublishStep = ({ eventId, previousStep, openInvitations }: Props) => {
  const { t } = useTranslation();
  const terms = useProcurementTerms();
  const navigate = useNavigate();
  const toast = useToast();
  const { trigger } = useApiError();
  const confirmationModal = useConfirmationModal();
  const { state } = useEditor<SourcingEventDto, SourcingEventPatchDto>(eventId);
  const { readinessFor, isPublishReady, isLoading: readinessLoading, isError: readinessError } = useReadinessByField(eventId);
  const { data: persistedEvent } = useGetSourcingEventQuery({ eventId });
  const publishOperation = persistedEvent?.operations?.find((operation) => operation.operation === "PublishSourcingEvent");
  const publishReason = publishOperation?.reason ? t(publishOperation.reason.code) : undefined;
  const [publishPrivate] = usePublishPrivateSourcingEventMutation();
  const [submitChange] = useSubmitEventChangeMutation();
  const [runSubmission, isSubmitting] = useExclusiveAction((submission: () => Promise<void>) => submission());
  const [cancelChange, { isLoading: isDiscarding }] = useCancelEventChangeMutation();
  const frameworkNoLongerActive = useFrameworkNoLongerActive(state);
  const changeDraft = openChangeDraft(state);
  const isChangeMode = changeDraft !== undefined;
  const changedAreas = new Set(changeDraft?.changedAreas ?? []);
  const wasChanged = (area: ChangedArea) => isChangeMode && changedAreas.has(area);

  const privateContext = state.eventContext.type === "Private" ? state.eventContext : null;
  const miniCompetitionFramework =
    state.eventContext.type === "MiniCompetition" ? state.eventContext.frameworkAgreementId : null;
  const settledTerms = useSettledTerms(miniCompetitionFramework);
  const { refetch: refetchFramework } = useGetFrameworkAgreementQuery(
    { frameworkAgreementId: miniCompetitionFramework ?? "" },
    { skip: !miniCompetitionFramework },
  );
  const offersOpenPublishing =
    !isChangeMode && state.eventContext.type === "Private" && state.status === SourcingEventStatus.Draft;
  const [openInSupplierPortal, setOpenInSupplierPortal] = useState(false);
  const publishesOpenly = offersOpenPublishing && openInSupplierPortal;
  const panelAtPublish =
    miniCompetitionFramework && !isChangeMode && state.status === SourcingEventStatus.Draft && settledTerms
      ? settledTerms.framework
      : null;
  const activeOnPanel = panelAtPublish && new Set(activePanel(panelAtPublish).map((member) => member.organizationId));
  const organizationInvites = invitedOrganizations(state).filter(
    (invite, index, invites) => invites.findIndex((other) => other.organizationId === invite.organizationId) === index,
  );
  const uncountedInvitees = activeOnPanel
    ? organizationInvites.filter((invite) => !activeOnPanel.has(invite.organizationId))
    : [];
  const contractEndedInvitees = (panelAtPublish?.panel ?? []).filter(
    (member) => member.contractEnded && uncountedInvitees.some((invite) => invite.organizationId === member.organizationId),
  );
  const organizationInviteCount = organizationInvites.length - uncountedInvitees.length;
  const miniCompetitionEmailInvites =
    persistedEvent?.eventContext.type === "MiniCompetition" ? persistedEvent.eventContext.emailInvites ?? [] : [];
  const emailedEntries = new Set(miniCompetitionEmailInvites.map((invite) => invite.organizationEntry?.id));
  const unregisteredOnPanel = panelAtPublish ? reachableUnregistered(panelAtPublish) : [];
  const notEmailed = unregisteredOnPanel.filter((member) => !emailedEntries.has(member.organizationEntryId));
  const reachedCount = organizationInviteCount + unregisteredOnPanel.length - notEmailed.length;
  const tooFewOnPanel =
    panelAtPublish !== null && activePanel(panelAtPublish).length + unregisteredOnPanel.length < 2;
  const tooFewInvited = panelAtPublish !== null && reachedCount < 2 && !tooFewOnPanel;
  const tooFewSuppliers = !panelAtPublish || reachedCount >= 2
    ? null
    : tooFewOnPanel
      ? tooFewForAMiniCompetition(t, panelAtPublish)
      : contractEndedInvitees.length > 0
        ? t(
            "The contract under the framework agreement has ended for {{suppliers}}, so it can no longer take part. Invite another supplier from the framework agreement under «Invitations».",
            { suppliers: contractEndedInvitees.map((member) => member.name).join(", ") },
          )
        : t(
            "Fewer than two of the invited suppliers are still active on the framework agreement. Invite another supplier from the framework agreement under «Invitations».",
          );
  const holdsPublishBack = frameworkNoLongerActive ?? tooFewSuppliers;
  const emailInvites = privateContext?.emailInvites ?? miniCompetitionEmailInvites;
  const emailInviteCount = emailInvites.length;
  const invitedCount = organizationInviteCount + emailedCompanyCount(emailInvites);
  const changedInvitations =
    isChangeMode && state.changeDraftInvitations && privateContext
      ? invitationChanges(state.changeDraftInvitations, privateContext.organizationInvites, privateContext.emailInvites ?? [])
      : [];
  const { data: textDocuments } = useListTextDocumentsForSourcingEventQuery({ eventId });
  const documentCount = (state.eventData.documents?.length ?? 0) + (textDocuments?.documents.length ?? 0);
  const { hasProductAccess } = useSub();
  const productsIncluded = hasProductAccess && resolveInclusions(state.eventData).products;
  const { data: productData } = useGetProductGroupsForSourcingEventQuery(
    { sourcingEventId: eventId },
    { skip: !productsIncluded },
  );
  const productGroups = productData?.productGroups ?? [];
  const productCount = productGroups.reduce((sum, group) => sum + group.products.length, 0);
  const awardCriteria = state.eventData.awardCriteria;
  const awardCriterionCount = awardCriteria?.bestPriceQualityRatio?.criteria?.length ?? 0;
  const awardModelLabel = awardCriteria?.lowestPrice
    ? t("Lowest price")
    : awardCriteria?.bestPriceQualityRatio
      ? t("Best price-quality ratio")
      : null;
  const minimumRequirementCount = state.eventData.minimumRequirements?.length ?? 0;
  const [deadlineStatus, ...otherDeadlineStatuses] = orderedByUnmet([
    readinessFor(FieldKeys.BidSubmissionDeadlinePresent),
    readinessFor(FieldKeys.BidSubmissionDeadlineInFuture),
  ]);
  // The badge follows `status` alone, and both award-criteria keys report the generic REQUIRED code
  // that SummaryField leaves to the badge. Lead with the first unmet one so an incomplete model is
  // always the one flagged — otherwise satisfied weights would show a green check while missing
  // descriptions silently hold Publish back.
  const criteriaIncluded = includesAwardCriteria(state.eventData);
  const requirementsIncluded = includesMinimumRequirements(state.eventData);
  const [awardCriteriaStatus, ...otherAwardCriteriaStatuses] = orderedByUnmet([
    ...(criteriaIncluded ? [readinessFor(FieldKeys.AwardCriteriaPresentWhenIncluded)] : []),
    ...(awardCriteria
      ? [readinessFor(FieldKeys.AwardCriteriaWeightsValid), readinessFor(FieldKeys.AwardCriteriaDescriptionsPresent)]
      : []),
  ]);

  const publish = () =>
    runSubmission(async () => {
      try {
        await coordinatorRegistry.flush(eventId);
        const published = await publishPrivate({ eventId, openInSupplierPortal: publishesOpenly }).unwrap();
        const leftOut = panelAtPublish
          ? Math.max(organizationInviteCount - invitedOrganizations(published).length, 0)
          : 0;
        toast({
          title: t("Event published!"),
          description:
            leftOut > 0
              ? t("Your event is now live, and the invitations have been sent. {{count}} suspended suppliers were not invited.", {
                  count: leftOut,
                })
              : t("Your event is now live, and the invitations have been sent"),
          status: "success",
        });
        navigate(urls.app.sourcingEvents.view.go(eventId));
      } catch (e) {
        if (isActiveSuppliersRefusal(e)) {
          void refetchFramework();
          return;
        }
        trigger(e);
      }
    });

  const sendChange = () =>
    runSubmission(async () => {
      if (!changeDraft) return;
      try {
        await coordinatorRegistry.flush(eventId);
        const published = await submitChange({ eventId, revisionId: changeDraft.id }).unwrap();
        const invitationsOnly =
          published.changedAreas?.length === 1 && published.changedAreas[0] === ChangedArea.Invitations;
        toast({
          title: t("The change has been published"),
          description: invitationsOnly
            ? t("The suppliers already invited were not notified.")
            : t("The invited suppliers have been told what changed."),
          status: "success",
        });
        navigate(urls.app.sourcingEvents.view.go(eventId));
      } catch (e) {
        trigger(e);
      }
    });

  const discardChange = () =>
    runSubmission(async () => {
      if (!changeDraft) return;
      try {
        await cancelChange({ eventId, revisionId: changeDraft.id }).unwrap();
        toast({ title: t("The change was discarded"), status: "success" });
        navigate(urls.app.sourcingEvents.view.go(eventId));
      } catch (e) {
        trigger(e);
      }
    });

  const confirmDiscardChange = () => {
    confirmationModal.show({
      title: t("Discard the change?"),
      description: t("The competition goes back to what the suppliers were last told."),
      confirmButtonText: t("Discard the change") ?? "",
      onConfirmed: discardChange,
    });
  };

  const confirmSendChange = () => {
    confirmationModal.show({
      title: t("Send the change?"),
      description: t(
        "The suppliers already invited are told what changed so they can update their bid. Adding an invitation on its own does not notify them.",
      ),
      confirmButtonText: t("Send the change") ?? "",
      onConfirmed: sendChange,
    });
  };

  const confirmPublish = () => {
    confirmationModal.show({
      title: t("Publish the procurement?"),
      description: publishesOpenly
        ? t("The invited organizations are notified immediately, and the procurement is published openly in the supplier portal.")
        : t("The invited organizations are notified immediately."),
      confirmButtonText: t("Publish") ?? "",
      onConfirmed: publish,
    });
  };

  return (
    <Box pb="8" pt="10">
      {confirmationModal.modal}
      {readinessError && (
        <Alert status="error" borderRadius="lg" mb="6">
          <AlertIcon />
          <Text fontSize="sm">
            {t("Could not load publish readiness — refresh the page to retry. Section status indicators below may not be accurate.")}
          </Text>
        </Alert>
      )}
      {isChangeMode && (
        <Alert status="info" borderRadius="md" mb="6">
          <AlertIcon />
          {t(
            "You are changing a published competition. The suppliers already invited are told what changed, unless the only change is a new invitation.",
          )}
        </Alert>
      )}
      <Box borderWidth="1px" borderRadius="lg" p="5" mb="8">
        <Heading size="md" mb="4">
          {isChangeMode ? t("Review the change before sending it") : t("Review your event before publishing")}
        </Heading>

        <SummaryField
          label={t("Title")}
          sourcePaths={["eventData.title"]}
          changed={wasChanged(ChangedArea.Title)}
          status={readinessFor(FieldKeys.TitlePresent)}
          value={state.eventData.title || <EmptyValue text={t("Not provided")} />}
        />
        <SummaryField
          label={t("Description")}
          sourcePaths={["eventData.description"]}
          changed={wasChanged(ChangedArea.Description)}
          status={readinessFor(FieldKeys.DescriptionPresent)}
          value={
            state.eventData.description && !isTiptapContentEmpty(state.eventData.description) ? (
              <Box
                borderLeft="2px solid"
                borderColor="smBorder"
                pl="3"
                maxH="60"
                overflowY="auto"
                overscrollBehavior="contain"
              >
                <TipTap content={state.eventData.description} editable={false} hasBorder={false} padding="0" />
              </Box>
            ) : (
              <EmptyValue text={t("Not provided")} />
            )
          }
        />
        <SummaryField
          label={t("Deadline")}
          sourcePaths={["eventData.bidSubmissionDeadline"]}
          changed={wasChanged(ChangedArea.Deadline)}
          status={deadlineStatus}
          extraStatuses={otherDeadlineStatuses}
          value={
            state.eventData.bidSubmissionDeadline ? (
              displayDateWithTime(state.eventData.bidSubmissionDeadline, t("at"))
            ) : (
              <EmptyValue text={t("Not provided")} />
            )
          }
        />
        {hasProductAccess && (
          <SummaryField
            label={t("Products")}
            changed={wasChanged(ChangedArea.Products)}
            status={productsIncluded ? readinessFor(FieldKeys.ProductsPresentWhenIncluded) : undefined}
            value={
              productGroups.length > 0 ? (
                <Text>
                  {t("{{count}} product groups", { count: productGroups.length })}
                  {` · ${t("{{count}} products", { count: productCount })}`}
                </Text>
              ) : (
                <EmptyValue text={productsIncluded ? t("No products added yet") : t("Not included")} />
              )
            }
          />
        )}
        {/* Both sections are optional, and a section left out carries no readiness badge. One the
            buyer switched on is held to the publish gate: it has to hold something. */}
        <SummaryField
          label={terms.awardCriteria}
          sourcePaths={["eventData.awardCriteria"]}
          status={awardCriteriaStatus}
          extraStatuses={otherAwardCriteriaStatuses}
          value={
            awardModelLabel ? (
              <Text>
                {awardModelLabel}
                {awardCriterionCount > 0
                  ? ` · ${terms.awardCriteriaCount(awardCriterionCount)}`
                  : ""}
              </Text>
            ) : (
              // "Not included" would contradict the switch the buyer just turned on — an included
              // section that is still empty is unfinished, not opted out.
              <EmptyValue text={criteriaIncluded ? terms.noAwardModelChosenYet : t("Not included")} />
            )
          }
        />
        {miniCompetitionFramework ? (
          <SummaryField
            label={t("Minimum requirements")}
            testId="summary-minimum-requirements"
            value={
              settledTerms?.registeredByHand
                ? t("Agreed in the framework agreement")
                : settledTerms
                  ? t("Settled in the framework competition ({{count}})", {
                      count: settledTerms.minimumRequirementCount,
                    })
                  : t("Settled in the framework competition")
            }
          />
        ) : (
          <SummaryField
            label={t("Minimum requirements")}
            sourcePaths={["eventData.minimumRequirements"]}
            testId="summary-minimum-requirements"
            changed={wasChanged(ChangedArea.MinimumRequirements)}
            status={requirementsIncluded ? readinessFor(FieldKeys.MinimumRequirementsPresentWhenIncluded) : undefined}
            value={
              minimumRequirementCount > 0 ? (
                t("{{count}} minimum requirements", { count: minimumRequirementCount })
              ) : (
                <EmptyValue text={requirementsIncluded ? t("None added yet") : t("Not included")} />
              )
            }
          />
        )}
        <SummaryField
          label={t("Invitations")}
          sourcePaths={["eventContext.organizationInvites"]}
          changed={wasChanged(ChangedArea.Invitations)}
          status={readinessFor(FieldKeys.AtLeastOneInvitation)}
          value={
            invitedCount > 0 ? (
              <Box>
                <Text>
                  {t("{{count}} organizations invited", { count: invitedCount })}
                  {emailInviteCount > 0 ? ` · ${t("{{count}} invited by email", { count: emailInviteCount })}` : ""}
                </Text>
                {uncountedInvitees.length > contractEndedInvitees.length && (
                  <Text fontSize="sm" color="smMuted">
                    {t("{{count}} suspended suppliers will not be invited", {
                      count: uncountedInvitees.length - contractEndedInvitees.length,
                    })}
                  </Text>
                )}
                {contractEndedInvitees.length > 0 && (
                  <Text fontSize="sm" color="smMuted">
                    {t("{{count}} suppliers whose contract under the framework agreement has ended will not be invited", {
                      count: contractEndedInvitees.length,
                    })}
                  </Text>
                )}
                {changedInvitations.length > 0 && <InvitationChangeList changes={changedInvitations} />}
              </Box>
            ) : (
              <EmptyValue text={t("No organizations have been invited yet.")} />
            )
          }
        />
        <SummaryField
          label={t("Documents")}
          sourcePaths={["eventData.documents"]}
          changed={wasChanged(ChangedArea.Documents)}
          value={
            documentCount > 0 ? (
              t("{{count}} documents attached", { count: documentCount })
            ) : (
              <EmptyValue text={t("No documents attached")} />
            )
          }
        />
      </Box>

      {isChangeMode && (
        <Flex justify="flex-start" mb="6">
          <Button variant="outline" colorScheme="red" onClick={confirmDiscardChange} isLoading={isDiscarding} isDisabled={isSubmitting}>
            {t("Discard the change")}
          </Button>
        </Flex>
      )}

      {notEmailed.length > 0 && (
        <Alert status="warning" rounded="md" fontSize="sm" alignItems="flex-start" mb="6">
          <AlertIcon />
          <Box>
            <Text>
              {t(
                "{{suppliers}} on the panel isn't registered in SourceMagnet and has no e-mail invite, so it won't hear about this mini-competition. Every supplier on the panel that can deliver should be contacted.",
                { suppliers: notEmailed.map((member) => member.name).join(", ") },
              )}
            </Text>
            {openInvitations && (
              <Button variant="link" colorScheme="teal" size="sm" mt="1" onClick={openInvitations}>
                {t("Go to Invitations")}
              </Button>
            )}
          </Box>
        </Alert>
      )}

      {holdsPublishBack && (
        <Alert status="warning" rounded="md" fontSize="sm" alignItems="flex-start" mb="6">
          <AlertIcon />
          <Box>
            <Text>{holdsPublishBack}</Text>
            {frameworkNoLongerActive === null && tooFewInvited && openInvitations && (
              <Button variant="link" colorScheme="teal" size="sm" mt="1" onClick={openInvitations}>
                {t("Go to Invitations")}
              </Button>
            )}
          </Box>
        </Alert>
      )}

      {offersOpenPublishing && (
        <Checkbox
          mt="8"
          alignItems="flex-start"
          isChecked={openInSupplierPortal}
          onChange={(event) => setOpenInSupplierPortal(event.target.checked)}
        >
          <Text fontWeight="medium">{t("Also publish openly in the supplier portal")}</Text>
          <Text fontSize="sm" color="smMuted">
            {t(
              "Anyone can then find it in the supplier portal, read it and download its attachments, also suppliers you have not invited. Copy its link with the share button at the top of the procurement once it is published.",
            )}
          </Text>
        </Checkbox>
      )}
      <StepNavigation
        onPrevious={previousStep}
        onNext={isChangeMode ? confirmSendChange : confirmPublish}
        nextLabel={isChangeMode ? t("Send the change") : t("Publish")}
        nextIsAction
        isNextDisabled={
          (!isChangeMode && !publishOperation?.allowed) ||
          readinessLoading ||
          readinessError ||
          !isPublishReady ||
          holdsPublishBack !== null ||
          isSubmitting
        }
        isNextLoading={isSubmitting && !isDiscarding}
        nextDisabledReason={
          holdsPublishBack ??
          (!isChangeMode && !publishOperation?.allowed
            ? publishReason
            : !isPublishReady && !readinessLoading && !readinessError
              ? t("Fill in all required fields to enable publishing")
              : undefined)
        }
        nextTooltip={
          !isChangeMode && !publishOperation?.allowed
            ? publishOperation?.reason && t(publishOperation.reason.code)
            : readinessError
            ? t("Could not load publish readiness — refresh the page to retry. Section status indicators below may not be accurate.")
            : !isPublishReady && !readinessLoading
            ? t("Fill in all required fields to enable publishing")
            : undefined
        }
      />
    </Box>
  );
};

const ACTIVE_SUPPLIERS_REFUSALS = new Set([
  "zz_FrameworkAgreementApiErrorCodeMiniCompetitionNeedsTwoActiveSuppliers",
  "zz_FrameworkAgreementApiErrorCodeMiniCompetitionNeedsTwoActiveInvitees",
]);

const isActiveSuppliersRefusal = (e: unknown): boolean =>
  isFetchBaseQueryError(e) && isApiErrorDto(e.data) && ACTIVE_SUPPLIERS_REFUSALS.has(e.data.errorCode);

const emailedCompanyCount = (invites: ReadonlyArray<{ id: string; organizationEntry?: { id: string } | null }>) =>
  new Set(invites.map((invite) => invite.organizationEntry?.id ?? invite.id)).size;
